<template>
  <div class="edit-bulk-submission-dialog-wrapper">
    <v-dialog
      :value="show"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      @input="$emit('update:show', $event)"
    >
      <v-card v-if="show">
        <DrawerToolbar title="Edit a Bulk Claim Upload" admin @close="handleClose" />
        <div v-if="bulkSubmission" class="drawer-container">
          <template v-if="!showSubmissionResult">
            <EditBulkSubmission
              :submit-loading="submitLoading"
              :mappedRows="mappedRows"
              :columns="columns"
              :mappedRowsErrors="mappedErrors"
              :onDeleteRow="handleDeleteRow"
              :onRevalidateAll="handleRevalidateAll"
              @submit-bulk-claims="handleSubmitBulkClaims"
            >
              <template #header>
                <div class="text-center">
                  <Typography variant="h-4">Bulk Submission {{ bulkSubmission.id }}</Typography>
                  <Typography variant="subtitle-1">
                    {{ bulkSubmissionFormattedCreatedDate }}</Typography
                  >
                </div>
              </template>
            </EditBulkSubmission>
          </template>

          <BulkSubmissionResult
            v-else
            :bulk-claim-submission-id="bulkSubmission.id"
            :success-count="submissionSuccessCount"
            :failed-count="submissionFailedCount"
            :invalid-count="submissionInvalidCount"
            @done="performClose"
            @fix-invalid-claims="showSubmissionResult = false"
          />
        </div>
      </v-card>
    </v-dialog>
    <Modal
      :value="showExitConfirmModal"
      @input="showExitConfirmModal = $event"
      :show-close-button="false"
      :onClose="onStayOnPage"
      data-testid="edit-bulk-submission-exit-confirm-modal"
    >
      <template #header>
        <Typography variant="h-5">Are you sure you want to exit?</Typography>
      </template>
      <Typography variant="body-1" class="text-left black--text pt-4">
        If you exit now, all your work will be lost.
      </Typography>
      <div class="d-flex justify-end pt-4">
        <Button text @click="onStayOnPage">Stay on page</Button>
        <Button text @click="onConfirmExit">Exit</Button>
      </div>
    </Modal>
  </div>
</template>
<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import EditBulkSubmission from "./EditBulkSubmission/EditBulkSubmission.vue";
import BulkSubmissionResult from "./BulkUploadDialog/BulkSubmissionResult.vue";
import getColumnsFromForm from "./mixins/getColumnsFromForm.js";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";
import moment from "moment";
import FormService from "@/services/FormService.js";

export default {
  name: "EditBulkSubmissionDialog",
  mixins: [getColumnsFromForm],
  components: {
    DrawerToolbar,
    EditBulkSubmission,
    Typography,
    Button,
    Modal,
    BulkSubmissionResult,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    bulkSubmission: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      bulkClaims: [],
      showSubmissionResult: false,
      submissionSuccessCount: 0,
      submissionFailedCount: 0,
      submissionInvalidCount: 0,
      submitLoading: false,
      mappedErrors: [],
      mappedRows: [],
      showExitConfirmModal: false,
    };
  },
  computed: {
    bulkSubmissionFormattedCreatedDate() {
      return moment(this.bulkSubmission?.createDate).format("MM/DD/YYYY");
    },
    needsExitConfirmation() {
      return !this.showSubmissionResult && !!this.bulkSubmission;
    },
  },
  methods: {
    handleClose() {
      if (this.needsExitConfirmation) {
        this.showExitConfirmModal = true;
        return;
      }
      this.performClose();
    },
    onStayOnPage() {
      this.showExitConfirmModal = false;
    },
    onConfirmExit() {
      this.performClose();
    },
    performClose() {
      this.showExitConfirmModal = false;
      this.showSubmissionResult = false;
      this.submissionSuccessCount = 0;
      this.submissionFailedCount = 0;
      this.submissionInvalidCount = 0;
      this.submitLoading = false;
      this.$emit("update:show", false);
    },
    async handleSubmitBulkClaims({ claims }) {
      if (!this.bulkSubmission?.formId) return;
      this.submitLoading = true;
      try {
        const response = await FormService.postBulkClaimSubmission(
          this.bulkSubmission.formId,
          claims,
          this.bulkSubmission.id
        );
        const successCount = response?.result?.successCount ?? 0;
        const failedCount = response?.result?.failedCount ?? 0;
        const bulkClaims = response?.result?.bulkClaimSubmission?.BulkClaims;
        this.submissionSuccessCount = successCount;
        this.submissionFailedCount = failedCount;
        this.submissionInvalidCount = Array.isArray(bulkClaims)
          ? bulkClaims.filter((claim) => claim.status === "Invalid").length
          : 0;
        this.showSubmissionResult = true;
        this.$emit("bulk-submission-submitted", this.bulkSubmission.id);
      } finally {
        this.submitLoading = false;
      }
    },
    async handleDeleteRow(rowId) {
      await FormService.deleteBulkClaimSubmission({
        bulkClaimSubmissionId: this.bulkSubmission.id,
        bulkClaimId: rowId,
        status: "Deleted",
      });
      this.bulkClaims = this.bulkClaims.filter((claim) => claim.id !== rowId);
    },
    async handleRevalidateAll(claims = []) {
      if (!this.bulkSubmission?.formId) return;

      this.mappedRows = claims;

      this.mappedErrors = await FormService.validateBulkClaimSubmission(
        this.bulkSubmission.formId,
        claims
      );
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.showExitConfirmModal = false;
          this.showSubmissionResult = false;
          this.submissionSuccessCount = 0;
          this.submissionFailedCount = 0;
          this.submissionInvalidCount = 0;
          this.submitLoading = false;
          return;
        }
        if (this.bulkSubmission?.formId) {
          this.getColumnsFromForm(this.bulkSubmission.formId);
        }
        if (this.bulkSubmission?.BulkClaims) {
          this.bulkClaims = this.bulkSubmission.BulkClaims;
        }
        this.mappedRows = this.bulkClaims.map((claim) => ({
          ...claim.claimJson,
          email: claim.claimantEmail,
          id: claim.id,
        }));
        this.mappedErrors = this.bulkClaims.map((claim) => ({
          uuid: claim.id,
          errors: claim.errorsJson,
        }));
      },
    },
  },
};
</script>

<style scoped>
.edit-bulk-submission-dialog-wrapper {
  display: contents;
}

.drawer-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 75px;
  padding-bottom: 24px;
  padding-left: 4rem;
  padding-right: 4rem;
  width: 100%;
}
</style>
